import React from "react";
import { Link } from "react-router-dom";
import Text, { TextVariant } from "@/components/ui/text";
import { cn } from "@/utils/utils";
import IconContainer from "@/components/ui/icon-container";
import { ArrowUpRight, SquareArrowUpRight } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";

// Everything but the value itself, which arrives as one of the two forms below.
export interface HoverActionProps {
  actionLabel: string;
  // The redesigned row shows a compact action ("Company" rather than "Edit in
  // Company"); the accessible name still comes from actionAriaLabel, so a
  // caller that supplies neither keeps the full label in both renderings.
  actionCompactLabel?: string;
  // Overrides the action's accessible name. Every row repeats the same visible
  // label, so a consumer rendering several must say which row each belongs to.
  // Include the visible label: aria-label replaces the name, it does not add.
  actionAriaLabel?: string;
  // Stable selector for the action itself. The wrapper carries a fixed one; this
  // is per-row, so a caller rendering several can target one of them.
  actionDataTest?: string;
  // The action renders only when a destination is supplied, so a caller whose
  // gate is closed (no linked entity, no permission) withholds `to`/`onAction`
  // rather than passing a flag — a variant cannot then bypass the gate.
  to?: string;
  // Replaces the history entry instead of pushing one — for a destination that
  // is another view of the page the row is already on, so Back still leaves the
  // page rather than appearing to do nothing.
  replace?: boolean;
  onAction?: () => void;
  actionIcon?: React.ReactNode;
  textVariant?: TextVariant;
  className?: string;
}

// The value is either plain text this control renders itself, or a rendering the
// caller supplies (file chips, for one) that the row only has to lay out. Exactly
// one of the two: a caller passing both would leave the row silently dropping one.
export type ValueProps =
  | { text: string; content?: never }
  | { text?: never; content: React.ReactNode };

type Props = HoverActionProps & ValueProps;

// The reveal lives on a wrapper, not on the control. `sr-only` keeps the action
// out of the layout while leaving it in the document so it can still take focus
// — `display: none` cannot, which is what left these fields with no keyboard
// route at all. It must be a wrapper: `not-sr-only` resets `white-space` and
// `height`, and its two-class selector outranks the button's own
// `whitespace-nowrap` and `h-8`, so applied directly the revealed action wraps
// and loses its height.
const ACTION_WRAPPER_CLASSNAME =
  "sr-only group-hover/cell:not-sr-only group-focus-within/cell:not-sr-only flex-shrink-0 opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 transition-opacity duration-200 ease-in-out";

// No flex-shrink-0 here: the wrapper below is the row's flex item now, not this
// control, so it would be inert.
const ACTION_CLASSNAME = "group/button flex w-fit";

// The redesigned action is a bordered secondary button sized to the row rather
// than a bare link, so it carries its own padding and radius.
const NEW_ACTION_CLASSNAME =
  "flex-shrink-0 h-7 pl-1 pr-2 gap-0.5 text-secondary rounded-md";

// The value yields its share of the row to the action once it is revealed, so a
// long value is truncated rather than pushing the action out of the row.
const VALUE_CLASSNAME = "min-w-0 flex-1 group-hover/cell:flex-initial";

/**
 * Read-only value with an action link revealed on row hover. The value itself is
 * never interactive — editing happens wherever the action sends the user.
 */
const TextWithHoverAction: React.FC<Props> = ({
  text,
  content,
  actionLabel,
  actionCompactLabel,
  actionAriaLabel,
  actionDataTest,
  to,
  replace,
  onAction,
  actionIcon,
  textVariant = "body",
  className
}) => {
  const isNewFieldsUI = useNewFieldsUI();

  // Both renderings build the same action from a link or a click handler; only
  // the icon, the label and the button style differ, so the wiring is written
  // once and the look is passed in.
  const renderAction = ({
    icon,
    label,
    buttonVariant,
    buttonClassName
  }: {
    icon: React.ReactNode;
    label: string;
    buttonVariant: "link" | "secondary";
    buttonClassName: string;
  }) => {
    const actionContent = (
      <>
        <IconContainer
          size={buttonVariant === "link" ? "5" : "sm"}
          color={buttonVariant === "link" ? "link" : "secondary"}
          className={
            buttonVariant === "link"
              ? "group-hover/button:text-link-hover transition-colors"
              : undefined
          }
        >
          {icon}
        </IconContainer>
        <Text
          as="span"
          variant={buttonVariant === "link" ? "body" : "bodySecondary"}
        >
          {label}
        </Text>
      </>
    );

    if (to) {
      return (
        <Button variant={buttonVariant} asChild className={buttonClassName}>
          <Link
            to={to}
            replace={replace}
            aria-label={actionAriaLabel}
            data-test={actionDataTest}
          >
            {actionContent}
          </Link>
        </Button>
      );
    }

    if (onAction) {
      return (
        <Button
          variant={buttonVariant}
          onClick={onAction}
          className={buttonClassName}
          aria-label={actionAriaLabel}
          data-test={actionDataTest}
        >
          {actionContent}
        </Button>
      );
    }

    return null;
  };

  const action = isNewFieldsUI
    ? renderAction({
        icon: actionIcon ?? <ArrowUpRight />,
        label: actionCompactLabel ?? actionLabel,
        buttonVariant: "secondary",
        buttonClassName: NEW_ACTION_CLASSNAME
      })
    : renderAction({
        icon: actionIcon ?? <SquareArrowUpRight />,
        label: actionLabel,
        buttonVariant: "link",
        buttonClassName: ACTION_CLASSNAME
      });

  return (
    <div
      className={cn(
        "group/cell flex transition-all duration-200 ease-in-out w-full justify-between items-center min-w-0",
        isNewFieldsUI
          ? "rounded-lg min-h-9 hover:bg-surface-hover-soft"
          : "rounded h-8 hover:bg-surface-hover-strong",
        className
      )}
    >
      {content !== undefined ? (
        <div className={VALUE_CLASSNAME}>{content}</div>
      ) : (
        <Text
          variant={textVariant}
          className={cn("truncate", VALUE_CLASSNAME)}
          hasTruncatedTooltip
        >
          {text}
        </Text>
      )}
      {action ? (
        <span
          data-test={
            actionDataTest ? `${actionDataTest}-wrapper` : "hover-action"
          }
          className={ACTION_WRAPPER_CLASSNAME}
        >
          {action}
        </span>
      ) : null}
    </div>
  );
};

export default TextWithHoverAction;
